import {
  type CreateInsurancePricingTierRequest,
  type CreateInsuranceRequest,
  type InsuranceApprovalRequest,
  type InsuranceDetail,
  InsuranceDetailSchema,
  type InsuranceListQuery,
  type InsuranceListResponse,
  InsuranceListResponseSchema,
  type InsurancePricingTierOut,
  InsurancePricingTierOutSchema,
  type UpdateInsurancePricingTierRequest,
  type UpdateInsuranceRequest,
} from '@utazzo/shared';
import { z } from 'zod';

import { apiClient } from '@/lib/api-client';

export const listInsurance = async (query: InsuranceListQuery): Promise<InsuranceListResponse> => {
  const { data } = await apiClient.get<unknown>('/insurance', { params: query });
  return InsuranceListResponseSchema.parse(data);
};

export const getInsurance = async (id: string): Promise<InsuranceDetail> => {
  const { data } = await apiClient.get<unknown>(`/insurance/${id}`);
  return InsuranceDetailSchema.parse(data);
};

export const createInsurance = async (
  payload: CreateInsuranceRequest,
): Promise<InsuranceDetail> => {
  const { data } = await apiClient.post<unknown>('/insurance', payload);
  return InsuranceDetailSchema.parse(data);
};

export const updateInsurance = async (
  id: string,
  payload: UpdateInsuranceRequest,
): Promise<InsuranceDetail> => {
  const { data } = await apiClient.patch<unknown>(`/insurance/${id}`, payload);
  return InsuranceDetailSchema.parse(data);
};

export const approveInsurance = async (
  id: string,
  payload: InsuranceApprovalRequest,
): Promise<InsuranceDetail> => {
  const { data } = await apiClient.post<unknown>(`/insurance/${id}/approval`, payload);
  return InsuranceDetailSchema.parse(data);
};

export const deleteInsurance = async (id: string): Promise<void> => {
  await apiClient.delete(`/insurance/${id}`);
};

export const bulkDeleteInsurance = async (
  ids: string[],
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/insurance/bulk-delete', { ids });
  return data;
};

// ---------- Pricing tier sub-resource ----------

const PricingTierListSchema = z.array(InsurancePricingTierOutSchema);

export const listInsurancePricingTiers = async (
  insuranceId: string,
): Promise<InsurancePricingTierOut[]> => {
  const { data } = await apiClient.get<unknown>(`/insurance/${insuranceId}/pricing-tiers`);
  return PricingTierListSchema.parse(data);
};

export const createInsurancePricingTier = async (
  insuranceId: string,
  payload: CreateInsurancePricingTierRequest,
): Promise<InsurancePricingTierOut> => {
  const { data } = await apiClient.post<unknown>(
    `/insurance/${insuranceId}/pricing-tiers`,
    payload,
  );
  return InsurancePricingTierOutSchema.parse(data);
};

export const updateInsurancePricingTier = async (
  insuranceId: string,
  tierId: string,
  payload: UpdateInsurancePricingTierRequest,
): Promise<InsurancePricingTierOut> => {
  const { data } = await apiClient.patch<unknown>(
    `/insurance/${insuranceId}/pricing-tiers/${tierId}`,
    payload,
  );
  return InsurancePricingTierOutSchema.parse(data);
};

export const deleteInsurancePricingTier = async (
  insuranceId: string,
  tierId: string,
): Promise<void> => {
  await apiClient.delete(`/insurance/${insuranceId}/pricing-tiers/${tierId}`);
};
